import { useActionConfirmation } from '../components/ActionFeedbackProvider';
// React and hooks
import React, { useRef, useState, useEffect } from 'react';
// Icons
import { Trash, Home, FileUp, FileDown, Sun, Moon, Pencil, Plus, Search, Users } from 'lucide-react';
// Animation
import { motion } from 'framer-motion';
// Router
import { useNavigate } from 'react-router-dom';
// Hooks
import useTheme from '../hooks/useTheme';
// API
import api from '../lib/api';
// i18n
import { useTranslation } from 'react-i18next';
import PageHeading from '../components/PageHeading';

const ClientScreen = () => {
  const fileInputRef = useRef();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentId, setCurrentId] = useState(null);
  const [clientForm, setClientForm] = useState({ name: '', contact: '', address: '', notes: '' });

  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const confirmAction = useActionConfirmation();

  // Base button styles (white/black with theme switch)
  const baseBtn =
    "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 " +
    "bg-black text-white hover:bg-white hover:text-black hover:scale-105 disabled:opacity-60 " +
    "dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white";

  // Fetch clients
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get('/clients');
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorLoadClients'));
    } finally {
      setLoading(false);
    }
  };

  // Modal functions
  const openCreate = () => {
    setModalMode('create');
    setCurrentId(null);
    setClientForm({ name: '', contact: '', address: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (client) => {
    setModalMode('edit');
    setCurrentId(client.id || client._id);
    setClientForm({
      name: client.name || '',
      contact: client.contact || '',
      address: client.address || '',
      notes: client.notes || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentId(null);
    setClientForm({ name: '', contact: '', address: '', notes: '' });
  };

  const handleSave = async () => {
    if (!clientForm.name.trim()) return;
    try {
      setBusyAction(true);
      if (modalMode === 'create') {
        await api.post('/clients', clientForm);
      } else if (modalMode === 'edit' && currentId) {
        await api.put(`/clients/${currentId}`, clientForm);
      }
      closeModal();
      await fetchClients();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorSaveClient'));
    } finally {
      setBusyAction(false);
    }
  };

  const handleDeleteClient = async (id) => {
    if (!await confirmAction(t('confirmDeleteClient'))) return;
    try {
      setBusyAction(true);
      await api.delete(`/clients/${id}`);
      await fetchClients();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorDeleteClient'));
    } finally {
      setBusyAction(false);
    }
  };

  // Import / Export (if needed later)
  const handleImportClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setBusyAction(true);
      await api.post('/clients/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(t('importClientsSuccess'));
      await fetchClients();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorImportClients'));
    } finally {
      setBusyAction(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    try {
      setBusyAction(true);
      const res = await api.get('/clients/export', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clients.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorExportClients'));
    } finally {
      setBusyAction(false);
    }
  };

  const filteredClients = clients
    .filter((client) => [client.name, client.contact, client.address, client.notes]
      .some((value) => String(value || '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())))
    .sort((first, second) => String(first.name || '').localeCompare(String(second.name || ''), undefined, { sensitivity: 'base' }) * (sortOrder === 'asc' ? 1 : -1));

  return (
    <motion.div
      className="min-h-full px-6 py-8 relative"
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Theme toggle */}
      <button onClick={toggleTheme} className="absolute top-4 right-4 text-black dark:text-white">
        {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
      </button>

      {/* Main card */}
      <div className="relative z-10 max-w-4xl mx-auto bg-white dark:bg-gray-800 bg-opacity-90 shadow-xl rounded-xl p-6 backdrop-blur-md">
        <PageHeading icon={<Users size={26} />} title={t('clients')} />
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full gap-2 md:w-auto">
            <label className="flex min-w-0 flex-1 items-center rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-700 md:w-64">
              <Search size={16} className="mr-2 text-gray-500 dark:text-gray-300" />
              <input type="search" aria-label={t('search')} placeholder={t('search')} value={search} onChange={(event) => setSearch(event.target.value)} className="w-full bg-transparent text-sm text-gray-700 outline-none dark:text-gray-200" />
            </label>
            <select aria-label={t('sortAlphabetically')} value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
              <option value="asc">{t('sortAZ')}</option>
              <option value="desc">{t('sortZA')}</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={openCreate} disabled={busyAction} className={baseBtn}>
              <Plus size={16} /> {t('add')}
            </button>
            <button onClick={handleImportClick} disabled={busyAction} className={baseBtn}>
              <FileUp size={16} /> {t('import')}
            </button>
            <button onClick={handleExport} disabled={busyAction} className={baseBtn}>
              <FileDown size={16} /> {t('export')}
            </button>
            <button onClick={() => navigate('/')} className={baseBtn}>
              <Home size={16} /> {t('home')}
            </button>
          </div>
        </div>

        <input type="file" accept=".xlsx" ref={fileInputRef} onChange={handleFileChange} className="hidden" />

        {error && <div className="mb-4 rounded-lg bg-red-600/90 text-white px-4 py-2">{error}</div>}

        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t('loading')}</div>
        ) : filteredClients.length > 0 ? (
          <div className="space-y-2">
            {filteredClients.map((c) => {
              const id = c.id || c._id;
              return (
                <div
                  key={id}
                  className="flex items-center justify-between border border-gray-300 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-700 shadow-sm"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{c.name}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{c.contact}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{c.address}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 italic">{c.notes}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(c)} className="p-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => handleDeleteClient(id)} className="p-2 rounded-md bg-red-600 hover:bg-red-700 text-white">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 p-6 rounded-md text-gray-600 text-center dark:text-gray-300">
            {clients.length > 0 ? t('noResults') : t('noClients')}
          </div>
        )}
      </div>

      {/* Modal create/edit */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md w-full max-w-sm space-y-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">
              {modalMode === 'create' ? t('addClient') : t('editClient')}
            </h2>

            <input
              type="text"
              placeholder={t('name')}
              value={clientForm.name}
              onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
              className="w-full border px-3 py-2 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
              disabled={busyAction}
            />

            <input
              type="text"
              placeholder={t('contact')}
              value={clientForm.contact}
              onChange={(e) => setClientForm({ ...clientForm, contact: e.target.value })}
              className="w-full border px-3 py-2 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
              disabled={busyAction}
            />

            <input
              type="text"
              placeholder={t('address')}
              value={clientForm.address}
              onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
              className="w-full border px-3 py-2 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
              disabled={busyAction}
            />

            <textarea
              placeholder={t('notes')}
              value={clientForm.notes}
              onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
              className="w-full border px-3 py-2 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600"
              disabled={busyAction}
            />

            <div className="flex justify-end gap-2">
              <button onClick={closeModal} className={baseBtn} disabled={busyAction}>
                {t('cancel')}
              </button>
              <button onClick={handleSave} className={baseBtn} disabled={busyAction}>
                {busyAction ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ClientScreen;
